import { formatInTimeZone } from "date-fns-tz";
import { parseStoredTimeOfDay } from "@shared/timeOfDay";

/**
 * Canonical serialization for a time-of-day: pinned to the Unix epoch date and
 * always expressed in UTC, e.g. `1970-01-01T12:00:00Z`.
 */
const TIME_OF_DAY_ISO = "yyyy-MM-dd'T'HH:mm:ssXXX";

/**
 * Hours of operation are wall-clock times, not instants — see `@shared/timeOfDay`
 * for the two encodings that exist in the data and why.
 *
 * Everything that reads these values takes the UTC components back out (see
 * `toZonedTime(value, "UTC")` in the display components), so the offset must
 * always be `Z`. Serializing in the editor's local zone instead stamps that
 * editor's offset onto the value, which is invisible to our own UI but shifts
 * the time for any API consumer that honours the offset.
 */
export const timeOfDayISO = (hour: number, minute: number) =>
  formatInTimeZone(
    Date.UTC(1970, 0, 1, hour, minute, 0, 0),
    "UTC",
    TIME_OF_DAY_ISO,
  );

/**
 * Re-serializes a stored time-of-day into the canonical form above, so it matches
 * the option values produced by `timeOfDayISO` whatever it was written with.
 *
 * Canonical values are re-expressed from their UTC wall clock; legacy ones are
 * re-pinned from their local wall clock onto the epoch date, so they become
 * selectable in the editor without needing a data migration first.
 */
export const toCanonicalTimeOfDay = (time: string) => {
  const stored = parseStoredTimeOfDay(time);
  if (!stored) return time;

  if (!stored.isEpochPinned) {
    return timeOfDayISO(stored.hours, stored.minutes);
  }

  return formatInTimeZone(time, "UTC", TIME_OF_DAY_ISO);
};
